import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

// ─── Accounts that always have premium access — never charged ────────────────
// Add email addresses here (lowercase) to grant complimentary access.
const EXEMPT_EMAILS = ['michelkiyoshi@outlook.com'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── On mount: listen for auth state changes ──────────────────────────────
  useEffect(() => {
    let initialSessionHandled = false;

    // 1. Register listener FIRST (Supabase best practice)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event, session?.user?.email);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Fire-and-forget — don't block loading on profile fetch
          fetchProfile(session.user.id);
        } else {
          setProfile(null);
        }

        // Mark loading done on the first event we receive
        if (!initialSessionHandled) {
          initialSessionHandled = true;
          setLoading(false);
        }
      }
    );

    // 2. Fallback: if onAuthStateChange hasn't fired within 2 s, unblock the UI
    const timeout = setTimeout(() => {
      if (!initialSessionHandled) {
        console.warn('Auth session check timed out — unblocking UI');
        initialSessionHandled = true;
        setLoading(false);
      }
    }, 2000);

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── After returning from Stripe Checkout, refresh the profile ────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscribed') === 'true') {
      // Give the webhook a moment to write to Supabase, then refresh
      const timer = setTimeout(async () => {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
          await fetchProfile(currentUser.id);
        }
        // Clean up the URL without triggering a full reload
        const clean = window.location.pathname;
        window.history.replaceState({}, '', clean);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch (or create) the Supabase profile row ───────────────────────────
  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile(data);
      } else {
        // Profile doesn't exist — get fresh user data for the insert
        const { data: { user: freshUser } } = await supabase.auth.getUser();
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert([
            {
              id: userId,
              username: freshUser?.email?.split('@')[0] || 'user',
              full_name: freshUser?.user_metadata?.full_name || '',
              email: freshUser?.email || '',
              subscription_tier: 'free',
            },
          ])
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
        } else {
          setProfile(newProfile);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // ── Auth helpers ─────────────────────────────────────────────────────────
  const signUp = async (email, password, username) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Ensures the verification link in the email redirects to the correct
        // production URL rather than whatever is set in the Supabase dashboard.
        emailRedirectTo: `${window.location.origin}/app`,
        data: { username: username || email.split('@')[0] },
      },
    });
    return { data, error };
  };

  const resendConfirmationEmail = async (email) => {
    return await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/app` },
    });
  };

  const signIn = async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setProfile(null);
    }
    return { error };
  };

  const resetPasswordForEmail = async (email) => {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
  };

  const updatePassword = async (newPassword) => {
    return await supabase.auth.updateUser({ password: newPassword });
  };

  const updateEmail = async (newEmail) => {
    return await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: `${window.location.origin}/app` },
    );
  };

  // ── Derived subscription flags ────────────────────────────────────────────
  // isExempt  → user is on the complimentary-access whitelist (never charged)
  // isPremium → user either paid OR is exempt
  const userEmail = (user?.email || '').toLowerCase().trim();
  const isExempt  = EXEMPT_EMAILS.includes(userEmail);
  const isPremium = isExempt || profile?.subscription_tier === 'premium';

  const value = {
    user,
    profile,
    loading,
    isPremium,
    isExempt,
    signUp,
    signIn,
    signOut,
    fetchProfile,
    resetPasswordForEmail,
    updatePassword,
    updateEmail,
    resendConfirmationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
